import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AppSettingsService } from './app-settings.service';
import { AuthService } from './auth.service';
import { LoggerService } from './logger.service';
import { VoiceRecordingResult } from './voice-capture.service';

export interface TranscriptionResult {
  text: string;
  durationSeconds: number;
}

/**
 * Sends recorded audio to the chat service for speech-to-text.
 *
 * Endpoint: POST {chat service origin}/transcriptions (multipart form).
 * While the backend endpoint is being built, the `voiceInputStub` feature
 * flag short-circuits to a canned transcript so the UI can be exercised
 * end-to-end without audio infrastructure.
 */
@Injectable({
  providedIn: 'root'
})
export class TranscriptionService {
  private static readonly STUB_TEXT =
    'Create an invoice for ABC International for two hundred and fifty thousand naira for consulting services.';

  constructor(
    private appSettings: AppSettingsService,
    private authService: AuthService,
    private logger: LoggerService
  ) {}

  async transcribe(recording: VoiceRecordingResult): Promise<TranscriptionResult> {
    if (this.appSettings.isFeatureEnabled('voiceInputStub')) {
      this.logger.info('TranscriptionService', 'transcribe', 'Stub mode - returning canned transcript', {
        durationSeconds: recording.durationSeconds
      });
      await new Promise(resolve => setTimeout(resolve, 1200));
      return {
        text: TranscriptionService.STUB_TEXT,
        durationSeconds: recording.durationSeconds
      };
    }

    const url = `${this.chatServiceOrigin()}/transcriptions`;
    const session = await firstValueFrom(this.authService.authSession$);
    const token = session?.token;
    if (!token) {
      throw new Error('Not authenticated');
    }

    const form = new FormData();
    const extension = recording.mimeType.includes('mp4') ? 'm4a' : 'webm';
    form.append('audio', recording.blob, `voice-input.${extension}`);
    form.append('mimeType', recording.mimeType);
    form.append('durationSeconds', String(recording.durationSeconds));

    this.logger.info('TranscriptionService', 'transcribe', 'Uploading audio for transcription', {
      url,
      mimeType: recording.mimeType,
      sizeBytes: recording.blob.size,
      durationSeconds: recording.durationSeconds
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form
    });

    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      this.logger.error('TranscriptionService', 'transcribe', 'Transcription request failed', {
        status: response.status,
        detail
      });
      throw new Error(`Transcription failed (${response.status})`);
    }

    const payload = await response.json() as { text?: string; durationSeconds?: number };
    if (!payload.text) {
      throw new Error('Transcription returned no text');
    }

    return {
      text: payload.text,
      durationSeconds: payload.durationSeconds ?? recording.durationSeconds
    };
  }

  private chatServiceOrigin(): string {
    return new URL(this.appSettings.chatUrl).origin;
  }
}
